import { useState, useEffect, type ReactNode } from 'react'
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  type User,
} from 'firebase/auth'

import { auth } from '../firebase'
import type {
  AuthContextType,
  AuthState,
  CustomClaims,
  UserRole,
} from '../types/auth.types'
import { mapFirebaseUser } from '../types/auth.types'
import { AuthContext } from '../contexts/AuthContext'

// Initialize providers
const googleProvider = new GoogleAuthProvider()
googleProvider.addScope('profile')
googleProvider.addScope('email')

const appleProvider = new OAuthProvider('apple.com')
appleProvider.addScope('email')

// Auth Provider Component
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // Check if we're in emulator mode
  const isEmulatorMode = import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true'

  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
    claimsLoading: false,
  })

  // Function to get custom claims for a user
  const fetchCustomClaims = async (user: User): Promise<CustomClaims> => {
    try {
      setAuthState((prev) => ({ ...prev, claimsLoading: true }))
      const tokenResult = await user.getIdTokenResult(true)
      return tokenResult.claims as CustomClaims
    } catch (error) {
      console.error('Error fetching custom claims:', error)
      return {}
    } finally {
      setAuthState((prev) => ({ ...prev, claimsLoading: false }))
    }
  }

  // Function to refresh custom claims
  const refreshClaims = async (): Promise<void> => {
    const currentUser = auth.currentUser
    if (currentUser) {
      const customClaims = await fetchCustomClaims(currentUser)
      const mappedUser = mapFirebaseUser(currentUser, customClaims)
      setAuthState((prev) => ({
        ...prev,
        user: mappedUser,
        claimsLoading: false,
      }))
    }
  }

  // Function to check if user has specific role
  const isUserRole = (role: UserRole): boolean => {
    if (!authState.user) return false
    return (
      authState.user.role === role ||
      (role === 'user' && authState.user.role === 'admin')
    )
  }

  // Function to require specific role (throws error if not met)
  const requireRole = (role: UserRole): boolean => {
    if (!authState.user) {
      throw new Error('User not authenticated')
    }
    if (!isUserRole(role)) {
      throw new Error(`Insufficient permissions. Required role: ${role}`)
    }
    return true
  }

  useEffect(() => {
    // Set up auth state listener
    const unsubscribe = onAuthStateChanged(auth, async (user: User | null) => {
      if (user) {
        // Fetch custom claims for authenticated user
        const customClaims = await fetchCustomClaims(user)
        const mappedUser = mapFirebaseUser(user, customClaims)

        setAuthState({
          user: mappedUser,
          loading: false,
          error: null,
          claimsLoading: false,
        })
      } else {
        setAuthState({
          user: null,
          loading: false,
          error: null,
          claimsLoading: false,
        })
      }
    })

    return () => {
      unsubscribe()
    }
  }, [])

  const signInWithGoogle = async (): Promise<void> => {
    try {
      setAuthState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }))

      const result = await signInWithPopup(auth, googleProvider)

      if (!result.user) {
        throw new Error('Sign-in completed but no user returned')
      }

      console.log('Google sign-in successful:', result.user.email)
    } catch (error: unknown) {
      const firebaseError = error as { code?: string; message?: string }
      let errorMessage = 'Failed to sign in with Google'

      // Handle common Firebase Auth errors
      switch (firebaseError.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Sign-in was cancelled. Please try again.'
          break
        case 'auth/popup-blocked':
          errorMessage =
            'Popup was blocked by your browser. Please allow popups for this site and try again.'
          break
        case 'auth/cancelled-popup-request':
          errorMessage =
            'Another sign-in popup is already open. Please close it and try again.'
          break
        case 'auth/network-request-failed':
          errorMessage =
            'Network error. Please check your internet connection and try again.'
          break
        case 'auth/too-many-requests':
          errorMessage =
            'Too many sign-in attempts. Please wait a moment and try again.'
          break
        case 'auth/internal-error':
          // Try to extract meaningful error from auth blocking functions
          if (
            firebaseError.message &&
            firebaseError.message.includes('deactivated')
          ) {
            errorMessage =
              'Your account has been deactivated. Please contact an administrator for assistance.'
          } else if (
            firebaseError.message &&
            firebaseError.message.includes('permission-denied')
          ) {
            errorMessage =
              'Access denied. Please contact an administrator for assistance.'
          } else {
            errorMessage =
              'Authentication failed. Please try again or contact support if the problem persists.'
          }
          break
        default:
          if (error instanceof Error) {
            errorMessage = error.message
          }
      }

      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
        claimsLoading: false,
      }))
      throw new Error(errorMessage)
    }
  }

  const signInWithApple = async (): Promise<void> => {
    try {
      setAuthState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }))

      const result = await signInWithPopup(auth, appleProvider)

      if (!result.user) {
        throw new Error('Sign-in completed but no user returned')
      }

      console.log('Apple sign-in successful:', result.user.email)
    } catch (error: unknown) {
      const firebaseError = error as { code?: string; message?: string }
      let errorMessage = 'Failed to sign in with Apple'

      // Handle common Firebase Auth errors
      switch (firebaseError.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Sign-in was cancelled. Please try again.'
          break
        case 'auth/popup-blocked':
          errorMessage =
            'Popup was blocked by your browser. Please allow popups for this site and try again.'
          break
        case 'auth/cancelled-popup-request':
          errorMessage =
            'Another sign-in popup is already open. Please close it and try again.'
          break
        case 'auth/network-request-failed':
          errorMessage =
            'Network error. Please check your internet connection and try again.'
          break
        case 'auth/too-many-requests':
          errorMessage =
            'Too many sign-in attempts. Please wait a moment and try again.'
          break
        case 'auth/internal-error':
          // Try to extract meaningful error from auth blocking functions
          if (
            firebaseError.message &&
            firebaseError.message.includes('deactivated')
          ) {
            errorMessage =
              'Your account has been deactivated. Please contact an administrator for assistance.'
          } else if (
            firebaseError.message &&
            firebaseError.message.includes('permission-denied')
          ) {
            errorMessage =
              'Access denied. Please contact an administrator for assistance.'
          } else {
            errorMessage =
              'Authentication failed. Please try again or contact support if the problem persists.'
          }
          break
        default:
          if (error instanceof Error) {
            errorMessage = error.message
          }
      }

      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
        claimsLoading: false,
      }))
      throw new Error(errorMessage)
    }
  }

  const signInWithEmail = async (
    email: string,
    password: string
  ): Promise<void> => {
    try {
      setAuthState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }))

      await signInWithEmailAndPassword(auth, email, password)
    } catch (error: unknown) {
      const firebaseError = error as { code?: string; message?: string }
      let errorMessage = 'Failed to sign in with email'

      // Handle common Firebase Auth errors
      switch (firebaseError.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email address.'
          break
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password. Please try again.'
          break
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address.'
          break
        case 'auth/user-disabled':
          errorMessage =
            'This account has been disabled. Please contact support.'
          break
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later.'
          break
        case 'auth/network-request-failed':
          errorMessage =
            'Network error. Please check your internet connection and try again.'
          break
        case 'auth/internal-error':
          // Try to extract meaningful error from auth blocking functions
          if (
            firebaseError.message &&
            firebaseError.message.includes('deactivated')
          ) {
            errorMessage =
              'Your account has been deactivated. Please contact an administrator for assistance.'
          } else if (
            firebaseError.message &&
            firebaseError.message.includes('permission-denied')
          ) {
            errorMessage =
              'Access denied. Please contact an administrator for assistance.'
          } else {
            errorMessage =
              'Authentication failed. Please try again or contact support if the problem persists.'
          }
          break
        default:
          if (error instanceof Error) {
            errorMessage = error.message
          }
      }

      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
        claimsLoading: false,
      }))
      throw new Error(errorMessage)
    }
  }

  const signUpWithEmail = async (
    email: string,
    password: string
  ): Promise<void> => {
    try {
      setAuthState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }))

      await createUserWithEmailAndPassword(auth, email, password)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create account'

      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
        claimsLoading: false,
      }))
      throw new Error(errorMessage)
    }
  }

  const signOut = async (): Promise<void> => {
    try {
      setAuthState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }))

      await firebaseSignOut(auth)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to sign out'

      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
        claimsLoading: false,
      }))
      throw new Error(errorMessage)
    }
  }

  const clearError = (): void => {
    setAuthState((prev) => ({ ...prev, error: null }))
  }

  const value: AuthContextType = {
    ...authState,
    signInWithGoogle,
    signInWithApple,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    clearError,
    refreshClaims,
    isUserRole,
    requireRole,
    isEmulatorMode,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
